<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from 'vue';
import 'leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LIcon, LTooltip } from '@vue-leaflet/vue-leaflet';
import Direction from '@/assets/direction.svg?component';
import Marker from '@/public/map/marker.svg?url';
import { usePosition } from '@/composables/usePosition';

interface Position {
  lat: number;
  lng: number;
}

interface Place {
  position: Position;
  name: string;
  link: string;
  icon: string;
}

const { place } = defineProps<{ place: Place }>();

const { isDesktop } = useDevice();
const { position } = usePosition();

const mapRef = ref<InstanceType<typeof LMap> | null>(null);
const mapIsReady = ref(false);

const center = computed<[number, number]>(() => [place.position.lat, place.position.lng]);

const userPosition = computed<[number, number] | null>(() => {
  return position.value && position.value.lat && position.value.lng
    ? [position.value.lat, position.value.lng] as [number, number]
    : null;
});

const bounds = computed(() => {
  const coordinates: [number, number][] = [[place.position.lat, place.position.lng]];
  if (userPosition.value) coordinates.push(userPosition.value);
  return coordinates.length > 1 ? L.latLngBounds(coordinates) : null;
});

const directionLink = computed(() => {
  const destination = `${place.position.lat},${place.position.lng}`;
  return userPosition.value
    ? `https://www.google.com/maps/dir/?api=1&origin=${userPosition.value.join(',')}&destination=${destination}`
    : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
});

const updateMapBounds = async () => {
  await nextTick();
  if (mapRef.value?.leafletObject) {
    const map = mapRef.value.leafletObject;
    if (bounds.value && bounds.value.isValid()) {
      map.fitBounds(bounds.value, { padding: [50, 50] });
    } else {
      map.setView(center.value, 13);
    }
  }
};

const onMapReady = () => {
  mapIsReady.value = true;
  updateMapBounds();
};

watch(bounds, () => {
  if (mapIsReady.value) {
    updateMapBounds();
  }
});
</script>

<template>
  <LMap @ready="onMapReady" ref="mapRef" class="relative z-0 h-[500px] w-full" :use-global-leaflet="true"
    :center="center" :bounds="bounds || undefined" :zoom="13"
    :options="{ zoomAnimation: true, scrollWheelZoom: false, contextMenu: false, dragging: isDesktop }">
    <LTileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution="&copy; <a href='http://osm.org/copyright'>OpenStreetMap</a> contributors" />
    <LMarker :lat-lng="center">
      <LIcon :icon-url="place.icon" :icon-size="[30, 30]" />
      <LTooltip>{{ place.name }}</LTooltip>
    </LMarker>
    <LMarker v-if="userPosition" :lat-lng="userPosition">
      <LIcon :icon-url="Marker" :icon-size="[30, 30]" />
      <LTooltip>Twoja lokalizacja</LTooltip>
    </LMarker>
    <template v-if="userPosition">
      <NuxtLink :to="directionLink" target="_blank" title="Jak dojechać?"
        class="absolute z-[999] !text-secondary text-16 left-1 bottom-1 flex items-center gap-[5px] bg-white px-[10px] py-[8px] shadow-[rgb(0_0_0/30%)_0_1px_4px_-1px]">
        Sprawdź jak dojechać
        <Direction class="fill-primary" />
      </NuxtLink>
    </template>
  </LMap>
</template>
